import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "preact/hooks";
import { GameState, GameStateEnum } from "../../shared/types/global.types";
import { useBetting } from "./useBetting";
import { useFreebets } from "./useFreebets";

const MAX_BET_AMOUNT = 20;

interface UseBetStateProps {
  slotId: 1 | 2;
  gameState: GameState;
}

export const useBetState = ({ slotId, gameState }: UseBetStateProps) => {
  const [betAmount, setBetAmount] = useState(1);
  const [isBetPlaced, setIsBetPlaced] = useState(false);
  const [isCashedOut, setIsCashedOut] = useState(false);
  const [hasPendingBet, setHasPendingBet] = useState(false);
  const [isFreebetWarningOpen, setIsFreebetWarningOpen] = useState(false);
  const [pendingBetAmount, setPendingBetAmount] = useState<number | null>(null);

  const pendingBetRef = useRef<{ amount: number } | null>(null);
  const lastQuickClickedRef = useRef<number | null>(null);

  const { freebetProgress, freebets } = useFreebets(slotId);

  const isClimbing = gameState === GameStateEnum.CLIMBING;
  const isCountdown = gameState === GameStateEnum.COUNTDOWN;
  const isCrashed = gameState === GameStateEnum.CRASHED;

  const onBetResult = useCallback((res: any) => {
    setIsBetPlaced(!!res?.success);
    if (!res?.success) setHasPendingBet(false);
  }, []);

  const onCashOut = useCallback((res: any) => {
    if (res?.success) {
      setIsCashedOut(true);
      setIsBetPlaced(false);
    }
  }, []);

  const onRoundCrash = useCallback(() => {
    setIsBetPlaced(false);
    setIsCashedOut(false);
  }, []);

  const onCancelBet = useCallback((res: any) => {
    if (res?.success) {
      setIsBetPlaced(false);
      setHasPendingBet(false);
    }
  }, []);

  const bettingHandlers = useMemo(
    () => ({ onBetResult, onCashOut, onRoundCrash, onCancelBet }),
    [onBetResult, onCashOut, onRoundCrash, onCancelBet]
  );

  const betting = useBetting(slotId, bettingHandlers);

  const handleQuickBet = useCallback(
    (amount: number) => {
      const isSameAsLast = lastQuickClickedRef.current === amount;
      const base = isSameAsLast ? betAmount + amount : amount;
      setBetAmount(Math.min(base, MAX_BET_AMOUNT));
      lastQuickClickedRef.current = amount;
    },
    [betAmount]
  );

  const handleBetAmountChange = useCallback((v: number) => {
    setBetAmount(v);
    lastQuickClickedRef.current = null;
  }, []);

  const executeBet = useCallback(
    (amount: number) => {
      if (isCountdown) {
        setIsBetPlaced(true);
        setIsCashedOut(false);
        betting.placeBet({ amount, slotId });
      } else {
        pendingBetRef.current = { amount };
        setHasPendingBet(true);
      }
    },
    [isCountdown, betting, slotId]
  );

  const handlePlaceBet = useCallback(() => {
    if (betAmount <= 0) return;

    const hasActiveFreebet = freebets && freebets.length > 0;
    const hasProgress = freebetProgress && freebetProgress.currentProgress > 0;

    if (hasActiveFreebet || hasProgress) {
      const trackedAmount = hasActiveFreebet
        ? freebets[0].amount
        : freebetProgress!.trackedAmount;

      if (betAmount !== trackedAmount) {
        setPendingBetAmount(betAmount);
        setIsFreebetWarningOpen(true);
        return;
      }
    }

    executeBet(betAmount);
  }, [betAmount, freebets, freebetProgress, executeBet]);

  const handleCashOut = useCallback(() => {
    if (isBetPlaced && !isCashedOut && isClimbing) {
      setIsCashedOut(true);
      betting.cashOut({ slotId });
      setIsBetPlaced(false);
    }
  }, [isBetPlaced, isCashedOut, isClimbing, betting, slotId]);

  const confirmFreebetWarning = useCallback(() => {
    setIsFreebetWarningOpen(false);
    if (pendingBetAmount !== null) {
      executeBet(pendingBetAmount);
      setPendingBetAmount(null);
    }
  }, [pendingBetAmount, executeBet]);

  const closeFreebetWarning = useCallback(() => {
    setIsFreebetWarningOpen(false);
    setPendingBetAmount(null);
  }, []);

  const cancelPendingBet = useCallback(() => {
    setHasPendingBet(false);
    pendingBetRef.current = null;
  }, []);

  // Process pending bet on countdown
  useEffect(() => {
    if (!isCountdown) return;
    setIsCashedOut(false);

    if (hasPendingBet && pendingBetRef.current) {
      const { amount } = pendingBetRef.current;
      betting.placeBet({ amount, slotId });
      setIsBetPlaced(true);
      setHasPendingBet(false);
      pendingBetRef.current = null;
    }
  }, [gameState, hasPendingBet, betting, slotId, isCountdown]);

  return {
    betAmount,
    isBetPlaced,
    isCashedOut,
    hasPendingBet,
    isFreebetWarningOpen,
    freebets,
    isClimbing,
    isCountdown,
    isCrashed,
    betting,
    freebetProgress,
    handleQuickBet,
    handleBetAmountChange,
    handlePlaceBet,
    handleCashOut,
    cancelPendingBet,
    confirmFreebetWarning,
    closeFreebetWarning,
    MAX_BET_AMOUNT,
  };
};
